import { useEffect, useRef, useState } from "react"
import { useLocation, useNavigate } from "react-router-dom"
import { t } from "i18next"
import { toast } from "react-toastify"

import LaunchButton from "@/components/LaunchButton"
import Button from "@/components/button"
import Dropdown from "@/components/dropdown"
import Link from "@/components/link"
import Modal from "@/components/modal"
import ModalConfirm from "@/components/modal-confirm"
import WizardSidePanel, { WIZARD_SIDE_PANEL_Z_INDEX } from "@/components/WizardSidePanel"
import TextInput from "@/components/text-input"
import ToggleButton from "@/components/toggle-button"
import { useAppDispatch, useAppSelector } from "@/core/root-reducer"
import establishmentStore from "@/entities/establishment/model/establishment.store"
import physicalRiskStore from "@/entities/physical-risk/model/physical-risk.store"
import populationStore from "@/entities/population/model/population.store"
import preventionActionStore from "@/entities/prevention-action/model/prevention-action.store"
import {
  createEmptyPreventionAction,
  getStatusLabelKey,
  isRpsPreventionAction,
  NEWLY_ADDED_ACTION_PARAM,
  PreventionAction,
  PreventionActionStatus
} from "@/entities/prevention-action/model/prevention-action.types"
import { getRpsScopeLabel } from "@/entities/prevention-action/model/prevention-action.utils"
import PhysicalRiskUnitsSelector from "@/entities/physical-risk/ui/PhysicalRiskUnitsSelector"
import PreventionActionMonthPlanner from "@/entities/prevention-action/ui/PreventionActionMonthPlanner"
import PreventionActionResponsibleModal from "./PreventionActionResponsibleModal"
import LoadingModal from "@/modals/loading.modal"
import { STATUS_SAVING } from "@/redux/_status.types"
import { User } from "@/redux/user.types"
import { askAssistantJson, getBulletAnswerInstruction, getShortAnswerInstruction } from "@/utils/assistant.utils"
import { describePreventionActions, describeRisk, describeRps } from "@/entities/physical-risk/model/physical-risk.prompt"
import PhysicalRiskEditModal from "@/entities/physical-risk/ui/modal/PhysicalRiskEditModal"
import RiskWizardOverview from "@/entities/physical-risk/ui/RiskWizardOverview"
import {
  getInPlacePreventionIds,
  getInPlacePreventionSignature,
  isPreventionOnlyReinforced,
  usePreventionLevelAssistant
} from "@/entities/physical-risk/hooks/usePreventionLevelAssistant"
import { getWizardStepLockReason } from "@/components/wizard.utils"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import {
  faCircleCheck,
  faCircleNotch,
  faHeart,
  faPen,
  faSyncAlt,
  faTrashAlt,
  faUser,
  faUserSlash,
  faWandMagicSparkles
} from "@fortawesome/free-solid-svg-icons"

//Les questions de la chouette se posent dans une modale classique ; la fiche s'edite ensuite dans
//un panneau lateral dont les cartes s'ouvrent une a la fois.
type Screen = "ASSISTANT_CHOICE" | "PANEL"
export type StepId = "DESCRIPTION" | "FUNCTIONS" | "STATUS" | "DATES" | "RESPONSIBLE" | "BUDGET"

interface Props {
  isEditing?: boolean
  //Creation d'une mesure RPS (sans risque) : ses fonctions de depart, vide pour tout l'etablissement
  rpsPopulationIds?: string[]
  initialStep?: StepId
  onReviewPrevention?: (value: number) => void
  //Ouverte depuis le wizard risque : c'est son "Continuer" qui reevalue le niveau de prevention
  isPreventionReviewDisabled?: boolean
  onClose: () => void
  onSaved?: () => void
}

type PreventionActionSuggestion = Pick<PreventionAction, "name" | "description">

//Declarees ici (et nulle part ailleurs) : le panneau les enchaine et les messages de verrou
//reprennent leurs libelles.
const WIZARD_STEPS: Array<{ id: StepId; title: string }> = [
  { id: "DESCRIPTION", title: "Description" },
  { id: "STATUS", title: "Statut" },
  { id: "DATES", title: "Dates" },
  { id: "RESPONSIBLE", title: "Responsable" },
  { id: "BUDGET", title: "Budget" }
]
//Une mesure RPS choisit ses fonctions : l'etape est consideree confirmee, le perimetre de depart etant valable
const RPS_WIZARD_STEPS: Array<{ id: StepId; title: string }> = [
  WIZARD_STEPS[0],
  { id: "FUNCTIONS", title: "Fonctions" },
  ...WIZARD_STEPS.slice(1)
]
//Le panneau lateral passe au-dessus des modales : ses propres modales filles doivent le depasser
const CHILD_MODAL_Z_INDEX = WIZARD_SIDE_PANEL_Z_INDEX + 1

const PERIODIC_INTERVALS = [
  { id: 1, name: "Tous les mois" },
  { id: 3, name: "Tous les 3 mois" },
  { id: 12, name: "Tous les ans" },
  { id: 24, name: "Tous les 2 ans" },
  { id: "custom", name: "Intervalle personnalisé" }
]
const PRESET_PERIODIC_INTERVALS = [1, 3, 12, 24]
const EXPERT_INTRO = "Tu es un expert en prévention des risques professionnels."
const MEASURE_NAME_LABEL = "Nom de la mesure"
const ACTION_DETAILS_LABEL = "Détail des actions"
//Un intitule de mesure s'affiche comme titre de carte : il tient sur une ligne
const MEASURE_NAME_EXAMPLES = ["Couteaux à lame sécurisée", "Formation manipulation couteaux"]
function normalizeSuggestion(value: any): PreventionActionSuggestion | null {
  //L'assistant peut repondre une mesure seule, un tableau ou une liste enveloppee : on retient la premiere
  const unwrapped = value?.preventionActions ?? value?.actions ?? value?.suggestions ?? value
  const first = Array.isArray(unwrapped) ? unwrapped[0] : unwrapped
  const name = String(first?.name ?? first?.nom ?? first?.mesure ?? first?.title ?? "").trim()
  const description = String(first?.description ?? first?.note ?? first?.notes ?? "").trim()
  return name ? { name, description } : null
}

//Marquer une mesure en place ne doit pas la dater dans le futur : la date planifiee a venir
//deviendrait une date de realisation a venir, et le formatter la compterait deja comme en place.
//Une date planifiee deja passee, elle, est un fait : on la garde.
function getAppliedDate(plannedDate: Date | null) {
  const today = new Date()
  if (!plannedDate) return today

  const planned = new Date(plannedDate)
  return planned < today ? planned : today
}

function formatMonth(date: Date | null) {
  if (!date) return "Non définie"
  return new Date(date).toLocaleDateString("fr-FR", { month: "long", year: "numeric" })
}

function formatRange(startDate: Date | null, endDate: Date | null) {
  if (!startDate && !endDate) return "Non définies"
  if (startDate && !endDate) return `À partir de ${formatMonth(startDate)}`
  if (!startDate && endDate) return `Jusqu'à ${formatMonth(endDate)}`
  return `Du ${formatMonth(startDate)} au ${formatMonth(endDate)}`
}

export default function PreventionActionAddModal({
  isEditing = false,
  rpsPopulationIds,
  initialStep,
  onReviewPrevention,
  isPreventionReviewDisabled,
  onClose,
  onSaved
}: Props) {
  const dispatch = useAppDispatch()
  const location = useLocation()
  const navigate = useNavigate()
  const draftRisk = useAppSelector(physicalRiskStore.selectors.draft)
  const physicalRisks = useAppSelector(physicalRiskStore.selectors.list)
  const action = useAppSelector(preventionActionStore.selectors.draft)
  const isRps = isEditing ? isRpsPreventionAction(action) : Boolean(rpsPopulationIds)
  const steps = isRps ? RPS_WIZARD_STEPS : WIZARD_STEPS
  const risk = isEditing ? (physicalRisks.find((item) => item.id === action.PhysicalRiskId) ?? draftRisk) : draftRisk
  const activeEstablishment = useAppSelector(establishmentStore.selectors.active)
  const establishmentId = isRps ? activeEstablishment.id : risk.EstablishmentId
  const populations = useAppSelector(populationStore.selectors.listForActiveEstablishment)
  const actionPopulations = populations.filter((population) => action.populationIds?.includes(population.id))
  const actionStatus = useAppSelector(preventionActionStore.selectors.status)
  const riskActions = useAppSelector((state) => preventionActionStore.selectors.listForRisk(state, risk.id))
  const rpsActions = useAppSelector((state) => preventionActionStore.selectors.listRps(state))
  const preventionActions = isRps ? rpsActions : riskActions
  const users = useAppSelector((state) => state.user.list)
  const activeUser = useAppSelector((state) => state.user.active)
  const uncheckedSteps = action.uncheckedSteps ?? []

  const [screen, setScreen] = useState<Screen>(isEditing ? "PANEL" : "ASSISTANT_CHOICE")
  //Creation : on arrive sur la description. Modification : rien d'ouvert, le panneau recapitule.
  const [openStepId, setOpenStepId] = useState<StepId | null>(initialStep ?? (isEditing ? null : "DESCRIPTION"))
  const [isAssistantLoading, setIsAssistantLoading] = useState(false)
  const [isResponsibleModalOpen, setIsResponsibleModalOpen] = useState(false)
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false)
  //Le champ numerique doit pouvoir etre vide pendant la saisie. La valeur metier reste un nombre
  //valide dans le brouillon, notamment si l'utilisateur enregistre sans quitter le champ.
  const [costInput, setCostInput] = useState(action.cost === null ? "" : String(action.cost))
  const budgetStepRef = useRef<HTMLDivElement>(null)
  //Niveau propose par l'assistant : sa presence remplace le panneau par l'etape Evaluation du risque
  const [reviewPreventionValue, setReviewPreventionValue] = useState<number | null>(null)
  const { isSuggestingPrevention, suggestPreventionLevel } = usePreventionLevelAssistant()
  const hasInitializedDraft = useRef(false)
  //Signature des mesures en place a l'ouverture : elle dit si le niveau de prevention est a revoir
  const preventionSignature = useRef(getInPlacePreventionSignature(preventionActions))
  //Mesures en place a l'ouverture : si aucune n'a disparu, le niveau retenu fait plancher
  const preventionInPlaceIds = useRef(getInPlacePreventionIds(preventionActions))

  useEffect(() => {
    setCostInput(action.cost === null ? "" : String(action.cost))
  }, [action.id])

  useEffect(() => {
    if (isEditing) return
    if (hasInitializedDraft.current) return
    hasInitializedDraft.current = true

    dispatch(
      preventionActionStore.actions.setActive(
        createEmptyPreventionAction({
          EstablishmentId: establishmentId,
          PhysicalRiskId: isRps ? null : risk.id,
          populationIds: rpsPopulationIds ?? [],
          UserId: getDefaultResponsibleId(),
          uncheckedSteps: WIZARD_STEPS.map((step) => step.id)
        })
      )
    )
  }, [])

  function isUserForEstablishment(user: User) {
    return user.EstablishmentId === establishmentId || (user.role === "ADMIN" && !user.EstablishmentId)
  }

  function getDefaultResponsibleId() {
    const establishmentUsers = users.filter(isUserForEstablishment)
    if (establishmentUsers.length === 1) return establishmentUsers[0].id
    if (establishmentUsers.some((user) => user.id === activeUser.id)) return activeUser.id
    return null
  }

  function handleChange(key: keyof PreventionAction, value: any) {
    dispatch(preventionActionStore.actions.edit({ key, value }))
  }

  function handleStatusChange(status: PreventionActionStatus) {
    const startedFallback = isEditing ? getAppliedDate(action.dateStartedPlanned) : null
    const doneFallback = isEditing ? getAppliedDate(action.datePlanned) : null
    handleChange("dateStarted", status === "todo" ? null : (action.dateStarted ?? startedFallback ?? new Date()))
    handleChange("dateDone", status === "done" ? (action.dateDone ?? doneFallback ?? new Date()) : null)
  }

  function selectStatus(isDone: boolean) {
    //Les deux choix representent « realisee » et « a faire » : recliquer le choix actif ne doit
    //surtout pas reecrire les dates historiques d'une mesure existante.
    if ((action.cStatus === "done") === isDone) return
    handleStatusChange(isDone ? "done" : "todo")
  }

  function getRiskContextPrompt() {
    //Une mesure RPS de tout l'etablissement repond aux situations de toutes ses fonctions
    return `${isRps ? describeRps(actionPopulations.length ? actionPopulations : populations) : describeRisk(risk)}

${describePreventionActions("Mesures déjà existantes", preventionActions)}`
  }

  /** Le hibou du nom : un intitule, et rien d'autre — les notes ont leur propre hibou */
  function getNameIdeaPrompt() {
    return `${EXPERT_INTRO}

À partir de cette fiche de risque, propose l'intitulé d'une mesure de prévention concrète.

${getRiskContextPrompt()}

La mesure doit être différente des mesures déjà existantes.

${getShortAnswerInstruction(MEASURE_NAME_EXAMPLES)}`
  }

  /** Compléter peut s'étendre un peu : c'est la proposition de départ qui doit être courte */
  function getNameImprovePrompt() {
    return `${EXPERT_INTRO}

${getRiskContextPrompt()}

Réécris en mieux l'intitulé de mesure de prévention placé entre <intitule> et </intitule>, sans changer la mesure dont il parle. Il peut gagner en précision, mais reste un intitulé : une seule ligne, jamais une phrase complète.

Ne réponds que par l'intitulé : pas de balises, pas de commentaire, et ne répète jamais ces consignes.

<intitule>
${action.name.trim()}
</intitule>`
  }

  /** Une proposition directement injectee dans le wizard */
  function getSuggestionPrompt() {
    return `${EXPERT_INTRO}

À partir de cette fiche de risque, propose une mesure de prévention concrète.

${getRiskContextPrompt()}

Retourne uniquement un JSON valide au format :
{ "preventionActions": [{ "name": "Intitulé court", "description": "• Détail 1\\n\\n• Détail 2" }] }

L'intitulé doit être court et actionnable. La description tient en 3 puces maximum, chacune commençant par le caractère « • » (jamais un tiret), de 15 mots au plus, séparées par une ligne vide (\\n\\n) : jamais un paragraphe.`
  }

  /** Detaille la mesure deja nommee, sans jamais proposer autre chose */
  function getNotesIdeaPrompt() {
    return `${EXPERT_INTRO}

Détaille la mise en œuvre de la mesure de prévention « ${action.name.trim()} », pour le risque suivant.

${getRiskContextPrompt()}

${getBulletAnswerInstruction()}

Reste concret et propre à cette mesure : ne propose pas une autre mesure et ne reformule pas son intitulé.`
  }

  function getNotesImprovePrompt() {
    return `${EXPERT_INTRO}

${getRiskContextPrompt()}

Réécris en mieux les notes de mise en œuvre de la mesure « ${action.name.trim()} » placées entre <notes> et </notes>, sans changer la mesure dont elles parlent ni inventer ce qui n'y est pas.

${getBulletAnswerInstruction()}

Ne réponds que par les notes réécrites : pas de balises, pas de commentaire, et ne répète jamais ces consignes.

<notes>
${action.description}
</notes>`
  }

  function generateSuggestion() {
    setIsAssistantLoading(true)

    askAssistantJson({
      preprompt:
        'Retourner uniquement un JSON valide au format { "preventionActions": [{ "name": "", "description": "" }] }.',
      prompt: getSuggestionPrompt(),
      onDone: (response) => {
        setIsAssistantLoading(false)
        const suggestion = normalizeSuggestion(response)
        if (!suggestion) {
          toast("Impossible de lire la suggestion de l'assistant.", { type: "error" })
          return
        }

        handleChange("name", suggestion.name)
        handleChange("description", suggestion.description)
        setScreen("PANEL")
      },
      onError: () => {
        setIsAssistantLoading(false)
        toast("L'assistant n'a pas pu proposer de mesures de prévention.", { type: "error" })
      }
    })
  }

  //Enregistrer ou supprimer une mesure change ce qui est reellement en place : on propose alors un
  //niveau de prevention, que l'utilisateur valide sur l'etape Evaluation du risque.
  async function reviewPreventionLevel(updatedActions: PreventionAction[]) {
    //Le RPS n'a pas de niveau de prevention a reevaluer
    if (isRps || isPreventionReviewDisabled || !risk.EstablishmentId) return false
    //Rien de nouveau en place (dates, budget, mesure seulement prevue) : rien a reevaluer
    if (getInPlacePreventionSignature(updatedActions) === preventionSignature.current) return false

    const suggestedValue = await suggestPreventionLevel(
      risk,
      updatedActions,
      isPreventionOnlyReinforced(preventionInPlaceIds.current, updatedActions) ? risk.prevention : undefined
    )
    if (suggestedValue === null) return false

    if (onReviewPrevention) {
      onReviewPrevention(suggestedValue)
      onClose()
      return true
    }
    setReviewPreventionValue(suggestedValue)
    return true
  }

  //La mesure creee est signalee par ?addedAction= : la checklist l'anime la ou elle est rendue.
  function markNewlyAdded(actionId: string) {
    const search = new URLSearchParams(location.search)
    search.set(NEWLY_ADDED_ACTION_PARAM, actionId)

    //Le param n'annote que la vue courante : inutile d'empiler un cran d'historique.
    navigate({ pathname: location.pathname, search: search.toString() }, { replace: true })
  }

  async function saveAction() {
    const response = isEditing
      ? await dispatch(preventionActionStore.thunks.updateEntity()).unwrap()
      : await dispatch(preventionActionStore.thunks.add(action)).unwrap()

    if (!response) return
    if (!isEditing) markNewlyAdded(response.id)

    //Le brouillon porte la version enregistree : c'est lui qui reflete ce qui est en place
    const updatedActions = isEditing
      ? preventionActions.map((item) => (item.id === action.id ? action : item))
      : preventionActions.concat(action)

    if (await reviewPreventionLevel(updatedActions)) return

    ;(onSaved ?? onClose)()
  }

  async function deleteAction() {
    const response = await dispatch(preventionActionStore.thunks.remove(action.id)).unwrap()
    if (!response) return

    setIsDeleteModalOpen(false)
    if (await reviewPreventionLevel(preventionActions.filter((item) => item.id !== action.id))) return

    onClose()
  }

  //Les etapes ne modifient que le brouillon : quitter sans enregistrer le restaure
  function handleCancelAndClose() {
    dispatch(preventionActionStore.actions.cancel())
    onClose()
  }

  //Le nom identifie la mesure : tant qu'il est vide, planifier ou budgeter n'a pas de sens.
  //L'etape ouverte n'est jamais verrouillee, sinon son contenu serait grise a l'ecran.
  function getStepLockReason(id: StepId) {
    if (id === openStepId || id === "DESCRIPTION" || action.name.trim()) return undefined

    return getWizardStepLockReason("le nom de la mesure", steps.find((step) => step.id === id)?.title ?? "")
  }

  function getStepSummary(id: StepId) {
    if (id === "DESCRIPTION") {
      return (
        <div className="flex flex-col gap-10 text-left text-base text-slate-900">
          <div>
            <div className="grey-t">{MEASURE_NAME_LABEL}</div>
            <div className="mt-[4px] whitespace-pre-wrap">{action.name || "Non défini"}</div>
          </div>
          <div>
            <div className="grey-t">{ACTION_DETAILS_LABEL}</div>
            <div className="mt-[4px] truncate whitespace-nowrap">{action.description || "Non défini"}</div>
          </div>
        </div>
      )
    }
    if (id === "FUNCTIONS") return getRpsScopeLabel(action, populations)
    if (id === "STATUS") return t(getStatusLabelKey(action.cStatus === "done"))

    if (id === "DATES") {
      const range =
        action.cStatus === "done"
          ? formatRange(action.dateStarted, action.dateDone)
          : formatRange(action.dateStartedPlanned, action.datePlanned)
      if (!action.refreshDelay) return range
      return `${range} · tous les ${action.cRefreshDelayLabel ?? `${action.refreshDelay} mois`}`
    }

    if (id === "RESPONSIBLE") {
      const responsible = users.find((user) => user.id === action.UserId)
      return responsible ? responsible.username || responsible.email : "Non défini"
    }

    if (action.cost === null) return "Non défini"
    const cost = `${new Intl.NumberFormat("fr-FR").format(action.cost)} €`
    return action.financedBy?.trim() ? `${cost} · ${action.financedBy.trim()}` : cost
  }

  function renderDescriptionStep() {
    return (
      <>
        <TextInput
          assistantIdeaPrompt={getNameIdeaPrompt()}
          assistantImprovePrompt={action.name.trim() ? getNameImprovePrompt() : undefined}
          title={MEASURE_NAME_LABEL}
          value={action.name}
          onChange={(event) => handleChange("name", event.value)}
          autoFocus
          isFullWidth
        />
        <div className="mt-20">
          <TextInput
            assistantIdeaPrompt={action.name.trim() ? getNotesIdeaPrompt() : undefined}
            assistantImprovePrompt={action.description?.trim() ? getNotesImprovePrompt() : undefined}
            title={ACTION_DETAILS_LABEL}
            isAutoGrow
            rows={5}
            value={action.description}
            onChange={(event) => handleChange("description", event.value)}
            isFullWidth
          />
        </div>
      </>
    )
  }

  function renderDatesStep() {
    return (
      <>
        <PreventionActionMonthPlanner
          startDate={action.dateStartedPlanned}
          endDate={action.datePlanned}
          onChange={(startDate, endDate) => {
            handleChange("dateStartedPlanned", startDate)
            handleChange("datePlanned", endDate)
          }}
        />

        <div className="mt-20 flex items-start gap-10">
          <ToggleButton
            isActive={action.refreshDelay !== null}
            onToogle={() => handleChange("refreshDelay", action.refreshDelay ? null : 12)}
          />
          <div>
            <button
              type="button"
              className="_hover border-0 bg-transparent p-0 text-left text-base font-normal text-slate-900"
              onClick={() => handleChange("refreshDelay", action.refreshDelay ? null : 12)}>
              Mesure périodique
            </button>
            <div className="grey-t mt-[6px] text-sm">
              Une mesure périodique doit être vérifiée selon un intervalle de temps défini, chaque année par exemple.
            </div>
          </div>
        </div>

        {action.refreshDelay !== null && (
          <div className="mt-20">
            <Dropdown
              active={PRESET_PERIODIC_INTERVALS.includes(action.refreshDelay) ? action.refreshDelay : "custom"}
              displayField="name"
              isFullWidth
              isContextMenuTop
              list={PERIODIC_INTERVALS}
              title="Intervalle"
              value={
                PRESET_PERIODIC_INTERVALS.includes(action.refreshDelay ?? -1) ? (
                  PERIODIC_INTERVALS.find((item) => item.id === action.refreshDelay)?.name
                ) : (
                  <div className="flex min-w-0 flex-1 items-center" onClick={(event) => event.stopPropagation()}>
                    <input
                      aria-label="Intervalle personnalisé en mois"
                      type="number"
                      min={1}
                      className="min-w-0 flex-1 border-0 bg-transparent p-0 outline-none"
                      value={action.refreshDelay}
                      onChange={(event) => handleChange("refreshDelay", Math.max(1, Number(event.target.value) || 1))}
                    />
                    <span className="grey-t ml-[6px] shrink-0">mois</span>
                  </div>
                )
              }
              onSelect={(item) => handleChange("refreshDelay", item.id === "custom" ? 6 : item.id)}
            />
          </div>
        )}
      </>
    )
  }

  function renderStatusStep() {
    return (
      <div className="flex gap-20">
        <LaunchButton
          description="La mesure est déjà réalisée"
          icon={faCircleCheck}
          isCta={action.cStatus === "done"}
          title={t(getStatusLabelKey(true))}
          onClick={() => selectStatus(true)}
        />
        <LaunchButton
          description="La mesure doit être planifiée"
          icon={faCircleNotch}
          isCta={action.cStatus !== "done"}
          title={t(getStatusLabelKey(false))}
          onClick={() => selectStatus(false)}
        />
      </div>
    )
  }

  function renderBudgetStep() {
    function toggleBudget() {
      const isBudgetDefined = action.cost !== null
      handleChange("cost", isBudgetDefined ? null : 0)
      setCostInput(isBudgetDefined ? "" : "0")
      if (isBudgetDefined) handleChange("financedBy", null)

      if (!isBudgetDefined) {
        //Attendre le rendu des champs nouvellement affiches, puis montrer le bas de la carte.
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            budgetStepRef.current?.closest("section")?.scrollIntoView({ behavior: "smooth", block: "end" })
          })
        })
      }
    }

    return (
      <div ref={budgetStepRef}>
        <div className="mb-10 flex items-center gap-10">
          <ToggleButton isActive={action.cost !== null} onToogle={toggleBudget} />
          <span className="text-base">Définir un budget</span>
        </div>
        {action.cost !== null && (
          <div>
            <div className="flex items-end gap-10">
              <TextInput
                min={0}
                type="number"
                title="Montant"
                value={costInput}
                width={180}
                onChange={(event) => {
                  setCostInput(event.value)
                  handleChange("cost", event.value === "" ? 0 : Math.max(0, Number(event.value) || 0))
                }}
                onBlur={() => setCostInput(String(action.cost ?? 0))}
              />
              <div className="grey-t mb-[12px] text-lg">€</div>
            </div>
            <div className="mt-20">
              <TextInput
                title="Qui finance cette mesure ?"
                value={action.financedBy ?? ""}
                onChange={(event) => handleChange("financedBy", event.value || null)}
                isFullWidth
              />
            </div>
          </div>
        )}
      </div>
    )
  }

  //Le bouton fait doublon avec le clic sur la photo : il rend l'action visible
  function renderResponsibleStep() {
    const responsible = users.find((user) => user.id === action.UserId)

    return (
      <div className="flex items-center gap-[14px]">
        {responsible?.imageUrl ? (
          <img alt="" className="h-[34px] w-[34px] shrink-0 rounded-full object-cover" src={responsible.imageUrl} />
        ) : (
          <div className="flex h-[34px] w-[34px] shrink-0 items-center justify-center rounded-full bg-slate-100">
            <FontAwesomeIcon className={responsible ? "grey-t" : "red-t"} icon={responsible ? faUser : faUserSlash} />
          </div>
        )}

        <div className="min-w-0 flex-1">
          <div className={`truncate ${responsible ? "" : "red-t"}`}>
            {responsible?.username || responsible?.email || "Aucun responsable défini"}
          </div>
          {/* Le nom affiche deja l'email quand l'utilisateur n'a pas de nom : pas de doublon */}
          {responsible?.username && responsible.email && (
            <div className="grey-t truncate text-sm">{responsible.email}</div>
          )}
        </div>

        <Link icon={faSyncAlt} isWithoutMargin onClick={() => setIsResponsibleModalOpen(true)}>
          {responsible ? "Changer" : "Définir"}
        </Link>
      </div>
    )
  }

  //Aucune fonction choisie : la mesure vaut pour tout l'etablissement
  function renderFunctionsStep() {
    const populationIds = action.populationIds
    const togglePopulation = (populationId: string) =>
      handleChange(
        "populationIds",
        populationIds.includes(populationId)
          ? populationIds.filter((id) => id !== populationId)
          : populationIds.concat(populationId)
      )

    return (
      <>
        <div className="grey-t mb-10 text-sm">Sans fonction choisie, la mesure concerne tout l’établissement.</div>
        <PhysicalRiskUnitsSelector
          unitType="population"
          selectedUnits={actionPopulations.map((population) => ({ type: "population" as const, population }))}
          onAddUnit={(unit) => unit.type === "population" && togglePopulation(unit.population.id)}
          onRemoveUnit={(unit) => unit.type === "population" && togglePopulation(unit.population.id)}
          onSelect={(unit) => unit.type === "population" && togglePopulation(unit.population.id)}
        />
      </>
    )
  }

  function renderStep(id: StepId) {
    if (id === "DESCRIPTION") return renderDescriptionStep()
    if (id === "FUNCTIONS") return renderFunctionsStep()
    if (id === "STATUS") return renderStatusStep()
    if (id === "DATES") return renderDatesStep()
    if (id === "RESPONSIBLE") return renderResponsibleStep()
    return renderBudgetStep()
  }

  //Ajout et modification suivent le meme parcours : confirmer ouvre l'etape suivante et la
  //derniere confirmation referme la carte. L'enregistrement reste une action globale distincte.
  function confirmStep() {
    const currentIndex = steps.findIndex((step) => step.id === openStepId)
    const currentStep = steps[currentIndex]
    const nextStep = steps[currentIndex + 1]

    if (currentStep && uncheckedSteps.includes(currentStep.id)) {
      handleChange(
        "uncheckedSteps",
        uncheckedSteps.filter((stepId) => stepId !== currentStep.id)
      )
    }

    if (!nextStep) {
      setOpenStepId(null)
      return
    }

    setOpenStepId(nextStep.id)
  }

  function renderStepFooter() {
    return (
      <>
        <Button isWithBorder onClick={() => setOpenStepId(null)}>
          Confirmer plus tard
        </Button>
        <Button
          className="primary"
          disabled={openStepId === "DESCRIPTION" && !action.name.trim()}
          onClick={confirmStep}>
          Confirmer
        </Button>
      </>
    )
  }

  function renderSaveFooter() {
    return (
      <>
        <Button isWithBorder onClick={handleCancelAndClose}>
          Annuler
        </Button>
        <Button
          className="primary"
          disabled={!action.name.trim()}
          isLoading={actionStatus === STATUS_SAVING}
          onClick={saveAction}>
          Enregistrer
        </Button>
      </>
    )
  }

  function handleOpenStepChange(id: string | null) {
    const stepId = id as StepId | null
    setOpenStepId(stepId)
  }

  //Les choix se posent tous dans la meme coquille : le risque et la question quand ils eclairent
  //le choix, puis les propositions. Fermer abandonne l'ajout, sauf indication contraire.
  function renderChoiceModal({
    children,
    footer,
    isLarge,
    isRiskVisible,
    onCloseChoice,
    question,
    title
  }: {
    children: React.ReactNode
    footer?: React.ReactNode
    isLarge?: boolean
    isRiskVisible?: boolean
    onCloseChoice?: () => void
    question?: string
    title?: string
  }) {
    return (
      <Modal
        title={title ?? (isEditing ? "Modifier une mesure de prévention" : "Ajouter une mesure de prévention")}
        isLarge={isLarge}
        disableKeyEvents
        isCloseButtonVisible
        onClose={onCloseChoice ?? onClose}
        footer={footer}>
        {isRiskVisible && <RiskWizardOverview risk={risk} />}
        {question && <div className="mb-20 text-lg">{question}</div>}
        {children}
      </Modal>
    )
  }

  if (isSuggestingPrevention) {
    return (
      <LoadingModal owlAssistantText="Je regarde les mesures en place et je propose un niveau de prévention adapté." />
    )
  }

  //La mesure est enregistree : le panneau laisse la place au risque, sur son etape Evaluation
  if (reviewPreventionValue !== null) {
    return (
      <PhysicalRiskEditModal
        initialStep="EVALUATION"
        risk={{ ...risk, prevention: reviewPreventionValue }}
        onClose={onClose}
      />
    )
  }

  if (isAssistantLoading) {
    return <LoadingModal owlAssistantText="Je prépare une mesure de prévention adaptée à cette fiche de risque." />
  }

  if (screen === "ASSISTANT_CHOICE") {
    return renderChoiceModal({
      children: (
        <div className="flex gap-20">
          <LaunchButton
            description="L'assistant propose des mesures de prévention adaptées au risque"
            icon={faWandMagicSparkles}
            iconBackground="#0E5AA7"
            isCta
            title="Utiliser l'assistant"
            onClick={generateSuggestion}
          />
          <LaunchButton
            description="Rédiger une mesure de prévention à partir d'une feuille blanche"
            icon={faPen}
            title="Ajouter moi-même"
            onClick={() => setScreen("PANEL")}
          />
        </div>
      )
    })
  }

  return (
    <>
      {isResponsibleModalOpen && (
        <PreventionActionResponsibleModal
          value={action.UserId}
          zIndex={CHILD_MODAL_Z_INDEX}
          onClose={() => setIsResponsibleModalOpen(false)}
          onSelect={(userId) => handleChange("UserId", userId)}
        />
      )}

      {isDeleteModalOpen && (
        <ModalConfirm
          zIndex={CHILD_MODAL_Z_INDEX}
          onNo={() => setIsDeleteModalOpen(false)}
          onYes={deleteAction}
          textBold="Cette action est irréversible."
          text={`Vous êtes sur le point de supprimer la mesure de prévention « ${action.name} ».`}
        />
      )}

      <WizardSidePanel
        title={isEditing ? "Modifier une mesure de prévention" : "Ajouter une mesure de prévention"}
        context={
          isRps ? (
            <div className="flex items-center gap-[14px]">
              <div className="flex h-[44px] w-[44px] shrink-0 items-center justify-center">
                <FontAwesomeIcon className="grey-t text-[28px]" icon={faHeart} />
              </div>
              <div className="min-w-0">
                <div className="grey-t text-base">Risque concerné</div>
                <div className="text-base leading-tight">Risques psychosociaux</div>
              </div>
            </div>
          ) : (
            <RiskWizardOverview isWithoutMargin risk={risk} />
          )
        }
        footer={openStepId === null ? renderSaveFooter() : undefined}
        footerStart={
          isEditing && openStepId === null ? (
            <Button isWithBorder icon={faTrashAlt} onClick={() => setIsDeleteModalOpen(true)}>
              Supprimer
            </Button>
          ) : undefined
        }
        stepFooter={renderStepFooter()}
        openStepId={openStepId}
        steps={steps.map((step) => {
          //Une etape non confirmee n'affiche pas de resume : la carte reste a remplir
          const isChecked = !uncheckedSteps.includes(step.id)

          return {
            content: renderStep(step.id),
            id: step.id,
            isChecked,
            lockReason: getStepLockReason(step.id),
            summary: isChecked ? getStepSummary(step.id) : undefined,
            summaryPosition: step.id === "DESCRIPTION" ? ("below" as const) : ("side" as const),
            title: step.title
          }
        })}
        onClose={handleCancelAndClose}
        onOpenStepChange={handleOpenStepChange}
      />
    </>
  )
}
